// PortfolioPastCollaborations — the portfolio's "Past collaborations" block
// (EPD-10120), ported from the media-kit-v2 prototype's BrandsSection /
// BrandLogo / CaseStudyPanel. A ruled grid of brand logos (EPD-12217), or
// wordmarks where there is no logo (EPD-10121), and,
// directly beneath it, a case-study panel for the active brand. Built from the
// creator's Brand Collaboration Highlights; it replaces the old Brand
// Highlights block and keeps its `highlights` section id. Themed via --pf-*.
//
// Layout follows the renderer's single container breakpoint
// (`@[30rem]/portfolio:`): 2 grid columns and a stacked panel on phones, 3
// columns with media beside the text above it.
//
// Published rows (EPD-12479): the public page never shows empty boxes. Rows
// hold up to 3 tiles that share the row's width, no tile is left alone on a
// row where that can be avoided, 2 brands share one row, and a single brand
// with details shows just its case study. The editor keeps the padded grid,
// whose empty boxes hint at adding another collaboration.

import { proxyImageUrl } from "@/lib/image-url";
import { collaborationPostLabel } from "@/lib/portfolio/post-a11y";
import {
  resolveBrandMark,
  type MarkBackground,
} from "@/lib/portfolio/brand-marks";
import {
  indexRecentPostImages,
  type RecentPostImage,
} from "@/lib/portfolio/post-thumbnail";
import {
  collaborationRows,
  fillerCount,
  toCollaborations,
  type Collaboration,
  type CollaborationMedia,
} from "@/lib/portfolio/collaborations";
import type {
  PublicBrandHighlight,
  PublicHighlightPostStats,
} from "@/lib/queries/public-profile";
import { EDGE_TOLERANCE, useScrollOverflow } from "@/hooks/use-scroll-overflow";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Link as LinkIcon,
  Link2,
} from "lucide-react";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { EmptyPrompt } from "./portfolio-empty-state";
import { PortfolioBrandMark } from "./portfolio-brand-mark";

type PhoneRowSize = 1 | 2;
type WideRowSize = 1 | 2 | 3;
// `collaborationRows` never makes a row longer than its column count, so each
// span map below needs a key for every size up to it. `satisfies` fails the
// build if a column count outgrows its row-size type, and the typed maps fail
// it if a size has no span.
const PHONE_COLUMNS = 2 satisfies PhoneRowSize;
const WIDE_COLUMNS = 3 satisfies WideRowSize;

// Literal class strings so Tailwind generates them: a filler shows only at the
// column count(s) whose last row it pads.
const FILLER_VISIBILITY = {
  both: "",
  phoneOnly: "@[30rem]/portfolio:hidden",
  wideOnly: "hidden @[30rem]/portfolio:block",
} as const;

// The published grid (EPD-12479) is 6 columns wide above the breakpoint, so
// rows of 3 and rows of 2 both divide it, and 2 on phones. Each tile spans its
// share of its row; literal strings so Tailwind generates them.
const PUBLISHED_GRID_COLUMNS = "grid-cols-2 @[30rem]/portfolio:grid-cols-6";
const WIDE_ROW_SPAN: Record<WideRowSize, string> = {
  1: "@[30rem]/portfolio:col-span-6",
  2: "@[30rem]/portfolio:col-span-3",
  3: "@[30rem]/portfolio:col-span-2",
};
const PHONE_ROW_SPAN: Record<PhoneRowSize, string> = {
  1: "col-span-2",
  2: "",
};

/** The size of the row each tile sits in, in tile order. */
function rowSizeOfEachTile(rows: readonly number[]): number[] {
  return rows.flatMap((size) => Array<number>(size).fill(size));
}

/** The column span for a tile in a row of `size`, from a map keyed 1..max. */
function rowSpan<Size extends number>(
  spans: Record<Size, string>,
  size: number,
): string | undefined {
  return isRowSize(spans, size) ? spans[size] : undefined;
}

function isRowSize<Size extends number>(
  spans: Record<Size, string>,
  size: number,
): size is Size {
  return Object.hasOwn(spans, size);
}

function BrandCell({
  collaboration,
  active,
  panelId,
  background,
  onSelect,
  className,
}: {
  collaboration: Collaboration;
  active: boolean;
  panelId: string;
  background: MarkBackground;
  onSelect: (key: string) => void;
  /** The tile's column span in the published grid (EPD-12479). */
  className?: string;
}) {
  const [cell, setCell] = useState<HTMLElement | null>(null);
  const mark = resolveBrandMark(
    { name: collaboration.name, logoUrl: collaboration.logoUrl },
    background,
  );
  const base = cn(
    "flex min-w-0 items-center justify-center overflow-hidden bg-[var(--pf-bg)] px-4 py-8",
    className,
  );

  if (!collaboration.hasCaseStudy) {
    return (
      <div ref={setCell} className={cn(base, "cursor-default")}>
        <PortfolioBrandMark mark={mark} cell={cell} />
      </div>
    );
  }

  return (
    <button
      ref={setCell}
      type="button"
      aria-pressed={active}
      aria-controls={panelId}
      onClick={() => onSelect(collaboration.key)}
      className={cn(
        base,
        "group cursor-pointer transition-colors duration-150 hover:bg-[var(--pf-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring aria-pressed:bg-[var(--pf-surface)]",
      )}
    >
      <PortfolioBrandMark
        mark={mark}
        cell={cell}
        className="group-hover:opacity-100 group-aria-pressed:opacity-100"
      />
    </button>
  );
}

/**
 * The `src` values to try for a post's thumbnail candidates: each one through
 * the /cdn proxy (a no-op for non-GCS hosts), then the original URL when the
 * proxy rewrote it, so a backup in a bucket the proxy can't read still loads.
 */
export function thumbnailSources(thumbnails: readonly string[]): string[] {
  const out: string[] = [];
  for (const url of thumbnails) {
    for (const src of [proxyImageUrl(url, 400), url]) {
      if (src && !out.includes(src)) out.push(src);
    }
  }
  return out;
}

/**
 * Each post link's position among the collaboration's post links, so links to
 * a brand's several posts get distinct names (EPD-12471).
 */
function postPositions(
  media: readonly CollaborationMedia[],
): Map<string, { index: number; total: number }> {
  const posts = media.filter((item) => item.kind === "post");
  return new Map(
    posts.map((item, index) => [item.key, { index, total: posts.length }]),
  );
}

function MediaAsset({
  media,
  brandName,
  position = { index: 0, total: 1 },
}: {
  media: CollaborationMedia;
  brandName: string;
  /** This post link's place among the brand's post links. */
  position?: { index: number; total: number };
}) {
  // Post thumbnails are hotlinked from social CDNs and can expire or refuse
  // the load; step through the remaining candidates, then fall back to the
  // link card rather than a broken image (EPD-12059). Failures are tracked by
  // src, not position: candidates can arrive later (the stats stream in after
  // paint) and shift the list.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());

  if (media.kind === "image") {
    return (
      <img
        src={proxyImageUrl(media.url, 400) ?? media.url}
        alt={media.alt}
        loading="lazy"
        className="block h-full w-full object-cover"
      />
    );
  }
  if (media.kind === "video") {
    return (
      // Muted + playsInline so mobile browsers render the first frame inline.
      <video
        src={media.url}
        controls
        muted
        playsInline
        preload="metadata"
        aria-label={media.label}
        className="block h-full w-full object-cover"
      />
    );
  }
  const src = thumbnailSources(media.thumbnails).find((s) => !failed.has(s));
  const linkLabel = collaborationPostLabel({
    brandName,
    host: media.host,
    ...position,
  });
  if (src) {
    return (
      <a
        href={media.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={linkLabel}
        className="relative block h-full w-full transition-opacity hover:opacity-90"
      >
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed((prev) => new Set(prev).add(src))}
          className="block h-full w-full object-cover"
        />
        <ExternalLink
          className="absolute right-2 top-2 h-3.5 w-3.5 text-white/90 drop-shadow"
          aria-hidden
        />
      </a>
    );
  }
  return (
    <a
      href={media.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={linkLabel}
      className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center transition-opacity hover:opacity-80"
      style={{ color: "var(--pf-text)" }}
    >
      <LinkIcon
        className="h-5 w-5"
        style={{ color: "var(--pf-muted)" }}
        aria-hidden
      />
      <span className="break-all text-sm font-medium">{media.host}</span>
      <span
        className="inline-flex items-center gap-1 text-xs"
        style={{ color: "var(--pf-muted)" }}
      >
        View post
        <ExternalLink className="h-3 w-3" aria-hidden />
      </span>
    </a>
  );
}

const mediaFrame =
  "aspect-[3/4] w-[200px] shrink-0 overflow-hidden rounded-2xl bg-[var(--pf-surface)]";

function MediaCarousel({ collaboration }: { collaboration: Collaboration }) {
  const trackRef = useRef<HTMLUListElement | null>(null);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  // Arrows only when the track overflows its own (container-sized) box. While
  // it does, both stay mounted so a focused arrow isn't removed under the
  // keyboard; the one with nowhere to go is disabled and invisible (EPD-12060).
  const [observeScroller, { canScrollPrev, canScrollNext }] =
    useScrollOverflow<HTMLUListElement>();
  const overflows = canScrollPrev || canScrollNext;
  const positions = postPositions(collaboration.media);
  const attachTrack = useCallback(
    (el: HTMLUListElement | null) => {
      trackRef.current = el;
      observeScroller(el);
    },
    [observeScroller],
  );
  const scrollByPage = (direction: number) => {
    const el = trackRef.current;
    if (!el) return;
    const target = el.scrollLeft + direction * el.clientWidth;
    const maxScroll = el.scrollWidth - el.clientWidth;
    // This page reaches the end: the pressed arrow is about to be disabled,
    // which would drop its focus, so hand focus to the opposite arrow. Same
    // edge tolerance as the disable rule, so a sub-pixel short landing still
    // counts as the end.
    const reachesEnd =
      direction > 0
        ? target >= maxScroll - EDGE_TOLERANCE
        : target <= EDGE_TOLERANCE;
    const opposite = direction > 0 ? prevRef.current : nextRef.current;
    if (reachesEnd && opposite) opposite.focus({ preventScroll: true });
    el.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };
  const navButton =
    "absolute top-1/2 z-[5] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="relative">
      <ul
        ref={attachTrack}
        aria-label={`${collaboration.name} media`}
        className="m-0 flex list-none gap-2.5 overflow-x-auto p-0 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {collaboration.media.map((media) => (
          <li
            key={media.key}
            className={mediaFrame}
            style={{ scrollSnapAlign: "start" }}
          >
            <MediaAsset
              media={media}
              brandName={collaboration.name}
              position={positions.get(media.key)}
            />
          </li>
        ))}
      </ul>
      {overflows && (
        <>
          <button
            ref={prevRef}
            type="button"
            aria-label="Previous"
            disabled={!canScrollPrev}
            onClick={() => scrollByPage(-1)}
            className={cn(
              navButton,
              "-left-1.5",
              !canScrollPrev && "invisible",
            )}
            style={{ background: "rgba(15,19,17,0.55)" }}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            ref={nextRef}
            type="button"
            aria-label="Next"
            disabled={!canScrollNext}
            onClick={() => scrollByPage(1)}
            className={cn(
              navButton,
              "-right-1.5",
              !canScrollNext && "invisible",
            )}
            style={{ background: "rgba(15,19,17,0.55)" }}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

export function CaseStudyPanel({
  id,
  collaboration,
}: {
  id: string;
  collaboration: Collaboration;
}) {
  const headingId = useId();
  const { media, stats } = collaboration;
  const single = media.length === 1 ? media[0] : null;

  return (
    <div
      id={id}
      role="region"
      aria-labelledby={headingId}
      className="mt-px flex flex-col overflow-hidden rounded-2xl border @[30rem]/portfolio:flex-row @[30rem]/portfolio:flex-wrap"
      style={{
        borderColor: "var(--pf-border)",
        background: "var(--pf-bg)",
        color: "var(--pf-text)",
      }}
    >
      {media.length > 1 && (
        // A carousel spans the panel's full width above the text, as in the
        // prototype; a single asset sits beside it. `min-w-0`: in the
        // flex-row layout the default min-width:auto grew this wrapper to the
        // media's full width, so the track never overflowed (no arrows, no
        // scroll) and the panel clipped the last items (EPD-12477).
        <div className="min-w-0 basis-full px-5 pt-5">
          <MediaCarousel collaboration={collaboration} />
        </div>
      )}
      {single && (
        <div
          className={cn(
            mediaFrame,
            "mx-5 mt-5 self-center @[30rem]/portfolio:mb-5 @[30rem]/portfolio:mr-0 @[30rem]/portfolio:self-start",
          )}
        >
          <MediaAsset
            key={single.key}
            media={single}
            brandName={collaboration.name}
          />
        </div>
      )}

      <div className="min-w-0 flex-1 wrap-break-word px-5 pb-5 pt-6 @[30rem]/portfolio:px-8 @[30rem]/portfolio:pt-7">
        {collaboration.eyebrow && (
          <div
            className="text-[11.5px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: "var(--pf-muted)" }}
          >
            {collaboration.eyebrow}
          </div>
        )}
        <h3
          id={headingId}
          style={{
            margin: "6px 0 14px",
            fontSize: "22px",
            fontWeight: 600,
            letterSpacing: "-0.01em",
          }}
        >
          {collaboration.name}
        </h3>
        {collaboration.summary && (
          <p
            className="whitespace-pre-line"
            style={{
              margin: stats.length > 0 ? "0 0 20px" : 0,
              fontSize: "var(--pf-body)",
              lineHeight: 1.6,
              color: "var(--pf-text)",
            }}
          >
            {collaboration.summary}
          </p>
        )}
        {stats.length > 0 && (
          <dl
            className="m-0 grid gap-2.5"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
            }}
          >
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col-reverse rounded-[10px] border px-3 py-3.5 text-center"
                style={{
                  borderColor: "var(--pf-border)",
                  background: "var(--pf-bg)",
                }}
              >
                <dt
                  className="mt-0.5 text-[11px]"
                  style={{ color: "var(--pf-muted)" }}
                >
                  {stat.label}
                </dt>
                <dd
                  className="m-0 text-[18px] font-bold tabular-nums"
                  style={{
                    fontFamily: "var(--pf-font-heading)",
                    color: "var(--pf-heading)",
                  }}
                >
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

interface PortfolioPastCollaborationsProps {
  highlights: readonly PublicBrandHighlight[];
  /** Read-through post stats + thumbnails (EPD-11945); may arrive later. */
  postStats?: PublicHighlightPostStats;
  /**
   * The creator's connected-account recent posts, a thumbnail source for collab
   * links that are among them (EPD-12059); may arrive later.
   */
  recentPosts?: readonly RecentPostImage[];
  surface: "editor" | "public";
  /**
   * How light the portfolio background is (`isLight(theme.bg)`), so brand
   * logos are requested in a colouring that reads on it (EPD-12217).
   */
  background?: MarkBackground;
  /** The Profile editor, for the empty-state CTA (editor surface only). */
  editHref?: string;
}

export function PortfolioPastCollaborations({
  highlights,
  postStats,
  recentPosts,
  surface,
  background = "light",
  editHref,
}: PortfolioPastCollaborationsProps) {
  const headingId = useId();
  const panelId = useId();
  const collaborations = useMemo(
    () =>
      toCollaborations(highlights, postStats, {
        recentPostImages: indexRecentPostImages(recentPosts ?? []),
      }),
    [highlights, postStats, recentPosts],
  );
  const [selected, setSelected] = useState<string | null>(null);

  if (collaborations.length === 0) {
    if (surface !== "editor") return null;
    return (
      <div className="rounded-2xl">
        <EmptyPrompt
          icon={Link2}
          message="Enter your past collaborations in your profile."
          cta={
            editHref
              ? { label: "Add past collaborations", href: editHref }
              : undefined
          }
        />
      </div>
    );
  }

  const openable = collaborations.filter((c) => c.hasCaseStudy);
  // The creator's pick if it still has content (data can change under it in
  // the live editor preview), otherwise the first brand with content.
  const active =
    openable.find((c) => c.key === selected) ?? openable[0] ?? null;

  const isEditor = surface === "editor";
  // One brand with details on the published page: its case study says it all,
  // so there's no strip to pick from (EPD-12479).
  const caseStudyOnly = !isEditor && collaborations.length === 1 && !!active;
  const phoneFillers = isEditor
    ? fillerCount(collaborations.length, PHONE_COLUMNS)
    : 0;
  const wideFillers = isEditor
    ? fillerCount(collaborations.length, WIDE_COLUMNS)
    : 0;
  const wideRowSizes = rowSizeOfEachTile(
    collaborationRows(collaborations.length, WIDE_COLUMNS),
  );
  const phoneRowSizes = rowSizeOfEachTile(
    collaborationRows(collaborations.length, PHONE_COLUMNS),
  );

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-6">
        <h2 id={headingId} style={{ margin: 0, fontWeight: 600 }}>
          Past collaborations
        </h2>
        {openable.length > 0 && !caseStudyOnly && (
          <p
            className="text-[13px]"
            style={{ margin: "6px 0 0", color: "var(--pf-muted)" }}
          >
            Click to view details
          </p>
        )}
      </div>

      {!caseStudyOnly && (
        <div
          className={cn(
            "grid gap-px overflow-hidden rounded-2xl border",
            isEditor
              ? "grid-cols-2 @[30rem]/portfolio:grid-cols-3"
              : PUBLISHED_GRID_COLUMNS,
          )}
          style={{
            background: "var(--pf-border)",
            borderColor: "var(--pf-border)",
          }}
        >
          {collaborations.map((c, i) => (
            <BrandCell
              key={c.key}
              collaboration={c}
              active={c.key === active?.key}
              panelId={panelId}
              background={background}
              onSelect={setSelected}
              className={
                isEditor
                  ? undefined
                  : cn(
                      rowSpan(PHONE_ROW_SPAN, phoneRowSizes[i]),
                      rowSpan(WIDE_ROW_SPAN, wideRowSizes[i]),
                    )
              }
            />
          ))}
          {Array.from(
            { length: Math.max(phoneFillers, wideFillers) },
            (_, i) => {
              const phone = i < phoneFillers;
              const wide = i < wideFillers;
              const visibility =
                phone && wide
                  ? FILLER_VISIBILITY.both
                  : phone
                    ? FILLER_VISIBILITY.phoneOnly
                    : FILLER_VISIBILITY.wideOnly;
              return (
                <div
                  key={`filler-${i}`}
                  aria-hidden="true"
                  data-filler={
                    phone && wide ? "both" : phone ? "phone" : "wide"
                  }
                  className={cn("bg-[var(--pf-bg)]", visibility)}
                />
              );
            },
          )}
        </div>
      )}

      {active && <CaseStudyPanel id={panelId} collaboration={active} />}
    </section>
  );
}
